<!-- Card wrapper: trigger row + optional footer action row share one bordered container -->
<div class="overflow-hidden rounded-xl border border-border bg-card">
<!-- Trigger row in sidebar -->
<div
  cdkOverlayOrigin
  #trigger="cdkOverlayOrigin"
  role="button"
  tabindex="0"
  [attr.aria-label]="'Select ' + label()"
  (click)="toggle()"
  (keydown.enter)="toggle()"
  (keydown.space)="toggle(); $event.preventDefault()"
  class="group flex w-full cursor-pointer items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/40"
>
  <span class="grid h-8 w-8 flex-none place-items-center rounded-lg bg-primary/10 text-primary">
    <hlm-icon [name]="icon()" size="1rem" />
  </span>
  <div class="flex min-w-0 flex-1 flex-col">
    @if (selectedEntityVm(); as vm) {
      <span class="truncate text-sm font-medium text-foreground">{{ vm.name }}</span>
      <span class="flex items-center gap-1.5 text-sm text-muted-foreground">
        <span class="font-mono-feat">UA {{ vm.uaId }}</span>
        <button
          type="button"
          (click)="copyId($event, 'ua')"
          [attr.aria-label]="'Copy UA ID'"
          class="grid h-4 w-4 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <hlm-icon [name]="copiedField() === 'ua' ? 'lucideCheck' : 'lucideCopy'" size="0.7rem" />
        </button>
        <span class="text-muted-foreground/40">·</span>
        <span class="font-mono-feat">PS {{ vm.psId }}</span>
        <button
          type="button"
          (click)="copyId($event, 'ps')"
          [attr.aria-label]="'Copy PS ID'"
          class="grid h-4 w-4 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <hlm-icon [name]="copiedField() === 'ps' ? 'lucideCheck' : 'lucideCopy'" size="0.7rem" />
        </button>
      </span>
    } @else {
      <span class="text-sm text-muted-foreground">Select {{ label() }}…</span>
    }
  </div>
  <hlm-icon
    [name]="isOpen() ? 'lucideChevronUp' : 'lucideChevronDown'"
    size="0.9rem"
    class="flex-none text-muted-foreground transition-colors group-hover:text-foreground"
  />
</div>

<!-- Optional footer action row (sibling of the trigger — its click does NOT toggle) -->
@if (footerLabel()) {
  <button
    type="button"
    (click)="footerAction.emit()"
    class="flex w-full items-center justify-center gap-2 border-t border-border px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring/40 focus:ring-inset"
  >
    <hlm-icon [name]="footerIcon()" size="0.9rem" />
    {{ footerLabel() }}
  </button>
}
</div>

<!-- Dropdown panel (CDK overlay) -->
<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="trigger"
  [cdkConnectedOverlayOpen]="isOpen()"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  (backdropClick)="close()"
  (detach)="close()"
>
  <div
    class="w-[560px] max-w-[90vw] overflow-hidden rounded-xl border border-border bg-popover shadow-[0_2px_4px_rgba(20,18,30,0.04),0_32px_64px_-16px_rgba(20,18,30,0.25)]"
  >
    <!-- Filter -->
    <div class="flex items-center gap-2 border-b border-border px-3 py-2.5">
      <hlm-icon name="lucideSearch" size="0.95rem" class="text-muted-foreground" />
      <input
        #filterInput
        type="text"
        class="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
        [placeholder]="'Filter ' + label() + '…'"
        [ngModel]="filterText()"
        (ngModelChange)="onFilterChange($event)"
        (keydown)="onKeydown($event)"
        (keydown.escape)="close()"
        role="combobox"
        [attr.aria-expanded]="isOpen()"
        aria-autocomplete="list"
        aria-controls="entity-selector-list"
        [attr.aria-activedescendant]="highlightedIndex() >= 0 ? 'entity-option-' + highlightedIndex() : null"
      />
      <span class="hidden items-center gap-1 font-mono-feat text-sm text-muted-foreground/70 sm:flex">
        <kbd class="rounded border border-border bg-muted px-1 py-0.5">↑</kbd>
        <kbd class="rounded border border-border bg-muted px-1 py-0.5">↓</kbd>
        nav
        <kbd class="ml-1 rounded border border-border bg-muted px-1 py-0.5">↵</kbd>
        select
      </span>
    </div>

    <!-- 3-column header -->
    <div class="grid grid-cols-[2fr_1fr_1fr] items-center gap-2 border-b border-border bg-muted/50 px-3 py-1.5 font-mono-feat text-sm uppercase tracking-[0.15em] text-muted-foreground">
      <span>User Account</span>
      <span>UA ID</span>
      <span>PS ID</span>
    </div>

    <!-- Entity list -->
    <div
      #listRef
      id="entity-selector-list"
      role="listbox"
      class="max-h-[360px] overflow-y-auto p-1"
    >
      @for (vm of filteredEntities(); track vm.entity.id; let i = $index) {
        <div
          [id]="'entity-option-' + i"
          role="option"
          tabindex="0"
          [attr.aria-selected]="selectedEntity()?.id === vm.entity.id"
          [attr.data-highlighted]="i === highlightedIndex() ? 'true' : 'false'"
          (click)="select(vm.entity)"
          (keydown.enter)="select(vm.entity)"
          (mouseenter)="highlightedIndex.set(i)"
          [class]="
            'grid grid-cols-[2fr_1fr_1fr] items-center gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer transition-colors ' +
            (selectedEntity()?.id === vm.entity.id ? 'bg-primary/10 ' : '') +
            (i === highlightedIndex() ? 'bg-muted ' : '') +
            (isDeactivated(vm.status) ? 'opacity-55 ' : '')
          "
        >
          <span class="truncate text-foreground">{{ vm.name }}</span>
          <span class="font-mono-feat text-[12px] text-muted-foreground">{{ vm.uaId }}</span>
          <span class="font-mono-feat text-[12px] text-muted-foreground">{{ vm.psId }}</span>
        </div>
      }
      @if (filteredEntities().length === 0) {
        <div class="px-3 py-6 text-center text-sm text-muted-foreground">
          No matches
        </div>
      }
    </div>
  </div>
</ng-template>
